import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import Swal from 'sweetalert2';
import { get_token, getCookie } from '@/router/index.js';
import {
  placeholder_configurations,
  placeholder_access_assignments,
  placeholder_launch_tests,
} from './platform_placeholders';
import {
  clone_default_readiness,
  build_checklist_entries,
  build_launch_test_rows,
  create_completed_readiness_result,
  create_failed_readiness_result,
  create_selected_readiness_result,
  format_status_label,
  get_api_error_message,
  get_copy_secret,
  get_display_secret,
  get_safe_redirect_url,
  get_status_badge_class,
} from './platform_management_helpers';
import {
  build_platform_management_headers,
  fetch_platforms_api,
  fetch_platform_configurations_api,
  // fetch_platform_access_assignments_api,
  submit_platform_form_api,
  submit_platform_config_form_api,
  submit_platform_access_form_api,
  run_readiness_check_api,
  fetch_platform_config_deployment_handover_api,
  reveal_platform_config_deployment_values_api,
  test_launch_api,
} from './platform_management_api';

function build_platform_form_payload(platform_form) {
  return {
    name: platform_form.name,
    app_code: platform_form.app_code,
    description: platform_form.description,
    status: platform_form.status,
  };
}

function build_platform_config_payload(platform_config_form, routing_rules, editing_configuration_id) {
  const payload = {
    platform_id: platform_config_form.platform_id || platform_config_form.platform,
    app_code: platform_config_form.app_code,
    environment: platform_config_form.environment,
    base_url: platform_config_form.base_url,
    launch_url: platform_config_form.launch_url,
    callback_url: platform_config_form.callback_url,
    signature_rules: {
      signer_salt: platform_config_form.signer_salt,
      max_allowed_skew: Number(platform_config_form.max_allowed_skew || 300),
      system: platform_config_form.system,
    },
    routing_rules,
  };

  if (editing_configuration_id) payload.platform_config_id = editing_configuration_id;
  return payload;
}

function build_platform_access_payload(access_form) {
  return {
    platform: access_form.platform,
    principal_name: access_form.principal_name,
    access_level: access_form.access_level,
    environment: access_form.environment,
  };
}

function build_deployment_handover_payload(configuration) {
  if (configuration?.id) return { platform_config_id: configuration.id };

  return {
    platform: configuration?.platform_id || configuration?.platform,
    app_code: configuration?.app_code,
  };
}

function normalize_env_entries(values = {}) {
  if (Array.isArray(values)) {
    return values.map((entry) => ({
      key: entry.key || entry.name || '',
      value: entry.value ?? entry.masked_value ?? '********',
    })).filter((entry) => entry.key);
  }

  return Object.entries(values || {}).map(([key, value]) => ({
    key,
    value: value?.value ?? value?.masked_value ?? value,
  }));
}

function normalize_required_routes(routes = []) {
  if (Array.isArray(routes)) return routes;
  return Object.entries(routes || {}).map(([label, value]) => ({ label, value }));
}

function build_env_prefix(app_code) {
  return String(app_code || 'PLATFORM')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function build_default_platform_form() {
  return {
    name: '',
    app_code: '',
    description: '',
    status: 'Connected',
  };
}

function first_truthy_value(values, fallback = '') {
  const found_value = values.find((value) => value);
  if (found_value) return found_value;
  return fallback;
}

function platform_config_platform_id_value(configuration) {
  return first_truthy_value([configuration?.platform_id, configuration?.platform]);
}

function platform_config_platform_value(configuration) {
  return first_truthy_value([configuration?.platform]);
}

function platform_config_app_code_value(configuration) {
  return first_truthy_value([configuration?.app_code]);
}

function platform_config_environment_value(configuration) {
  return first_truthy_value([configuration?.environment], 'development');
}

function platform_config_base_url_value(configuration) {
  return first_truthy_value([configuration?.base_url]);
}

function platform_config_launch_url_value(configuration) {
  return first_truthy_value([configuration?.launch_url]);
}

function platform_config_callback_url_value(configuration) {
  return first_truthy_value([configuration?.callback_url]);
}

function platform_config_signer_salt_value(configuration) {
  return first_truthy_value([configuration?.signer_salt]);
}

function platform_config_max_allowed_skew_value(configuration) {
  return first_truthy_value([configuration?.max_allowed_skew], 300);
}

function platform_config_system_value(configuration) {
  return first_truthy_value([configuration?.system]);
}

function platform_config_routing_rules_text_value(configuration) {
  return first_truthy_value([configuration?.routing_rules_text], '[]');
}

function platform_config_status_value(configuration) {
  return first_truthy_value([configuration?.status], 'Draft');
}

function build_default_platform_config_form(configuration = null) {
  return {
    platform_id: platform_config_platform_id_value(configuration),
    platform: platform_config_platform_value(configuration),
    app_code: platform_config_app_code_value(configuration),
    environment: platform_config_environment_value(configuration),
    base_url: platform_config_base_url_value(configuration),
    launch_url: platform_config_launch_url_value(configuration),
    callback_url: platform_config_callback_url_value(configuration),
    signer_salt: platform_config_signer_salt_value(configuration),
    max_allowed_skew: platform_config_max_allowed_skew_value(configuration),
    system: platform_config_system_value(configuration),
    routing_rules_text: platform_config_routing_rules_text_value(configuration),
    status: platform_config_status_value(configuration),
  };
}

function build_default_access_form(selected_configuration = null) {
  return {
    platform: selected_configuration?.platform || '',
    principal_name: '',
    access_level: 'Launch + Configure',
    environment: selected_configuration?.environment || 'UAT',
  };
}

function build_default_component_state() {
  return {
    csrf_token: '',
    active_tab: 'Configurations',
    loading: false,
    readiness_loading: false,
    launch_loading_id: null,
    deployment_handover_loading: false,
    platform_config_step: 1,
    selected_configuration_id: null,
    selected_configuration: null,
    selected_configuration_snapshot: null,
    deployment_checklist_modal_config: null,
    deployment_handover: null,
    deployment_values_revealed: false,
    revealed_deployment_values: null,
    editing_configuration_id: null,
    platforms: [],
    platform_configurations: [],
    access_assignments: [],
    launch_test_rows: [],
    platforms_loaded: false,
    configurations_loaded: false,
    access_assignments_loaded: false,
    launch_tests_loaded: false,
    readiness_result: clone_default_readiness(),
    platform_form: build_default_platform_form(),
    platform_config_form: build_default_platform_config_form(),
    access_form: build_default_access_form(),
  };
}

export default {
  name: 'PlatformManagementView',
  components: {
    Sidebar,
    Navbar,
  },

  data: () => build_default_component_state(),

  computed: {
    displayed_platforms() {
      return this.platforms;
    },

    displayed_configurations() {
      return this.configurations_loaded ? this.platform_configurations : placeholder_configurations;
    },

    displayed_access_assignments() {
      return this.access_assignments_loaded ? this.access_assignments : placeholder_access_assignments;
    },

    displayed_launch_tests() {
      return this.launch_tests_loaded ? this.launch_test_rows : placeholder_launch_tests;
    },

    connected_platforms_count() {
      return this.displayed_platforms.length;
    },

    active_configurations_count() {
      return this.displayed_configurations.filter((item) => ['Ready', 'Needs Review', 'Active'].includes(item.status)).length;
    },

    ready_for_launch_count() {
      return this.displayed_configurations.filter((item) => item.status === 'Ready').length;
    },

    access_assignments_count() {
      return this.displayed_access_assignments.length;
    },

    platform_options() {
      return this.displayed_platforms.map((platform) => ({
        id: platform.id,
        label: platform.name,
        app_code: platform.app_code || platform.name,
        environment: platform.environment || '',
      }));
    },

    readiness_panel_title() {
      return this.readiness_result.selected_platform_name || 'No configuration selected';
    },

    checklist_entries() {
      return build_checklist_entries(this.selected_checklist);
    },

    selected_checklist() {
      return this.selected_configuration?.checklist || {};
    },

    selected_config() {
      return this.selected_configuration;
    },

    current_handover() {
      return this.deployment_handover || {};
    },

    handover_platform_name() {
      return this.current_handover.platform_name || this.selected_configuration?.platform_name || 'No configuration selected';
    },

    handover_environment() {
      return this.current_handover.environment || this.selected_configuration?.environment || 'Not selected';
    },

    handover_app_code() {
      return this.current_handover.app_code || this.selected_configuration?.app_code || 'Not configured';
    },

    handover_status() {
      return this.current_handover.status || this.current_handover.handover_status || 'Ready for DevOps Handover';
    },

    handover_required_routes() {
      const routes = this.current_handover.required_platform_routes || this.current_handover.required_routes || [];
      return normalize_required_routes(routes);
    },

    handover_notes() {
      const notes = this.current_handover.notes || this.current_handover.devops_notes || [];
      if (Array.isArray(notes) && notes.length) return notes;
      if (typeof notes === 'string' && notes.trim()) return [notes];
      return [
        'Add these values to the one client/environment .env file.',
        'Deploy using the shared YAML/docker-compose file.',
        'Run readiness checks after deployment.',
      ];
    },

    displayed_deployment_env_values() {
      const values = this.deployment_values_revealed
        ? this.revealed_deployment_values
        : this.current_handover.masked_env_values || this.current_handover.env_values || {};
      const entries = normalize_env_entries(values);
      if (entries.length) return entries;

      const prefix = build_env_prefix(this.handover_app_code);
      return [
        { key: `${prefix}_INTERNAL_API_KEY`, value: '********' },
        { key: `${prefix}_SSO_SHARED_SECRET`, value: '********' },
        { key: `${prefix}_MAX_ALLOWED_SKEW`, value: this.selected_configuration?.max_allowed_skew || 300 },
        { key: `${prefix}_SYSTEM`, value: this.selected_configuration?.system || this.selected_configuration?.app_code || 'Not configured' },
      ];
    },
  },

  mounted() {
    this.csrf_token = getCookie('csrftoken');
    this.load_initial_state();
  },

  methods: {
    require_token_or_redirect() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return null;
      }
      return token;
    },

    build_headers(token, include_page_url = true) {
      return build_platform_management_headers(token, this.csrf_token, include_page_url);
    },

    async load_initial_state() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      this.loading = true;

      try {
        await Promise.all([
          this.fetch_platforms(token),
          this.fetch_platform_configurations(token),
          // this.fetch_platform_access_assignments(token),
        ]);

        this.build_launch_test_rows();
        this.initialize_selected_configuration();
      } finally {
        this.loading = false;
      }
    },

    async fetch_platforms(token) {
      const result = await fetch_platforms_api(this.build_headers(token)).catch(() => null);
      if (!result?.ok) {
        this.platforms = [];
        this.platforms_loaded = true;
        this.$notyf.error(get_api_error_message(result?.response_data, 'Unable to load platforms right now.'));
        return;
      }

      const platforms = Array.isArray(result.response_data?.data) ? result.response_data.data : [];
      this.platforms = platforms.map((platform) => ({
        id: platform.id,
        name: platform.name,
        app_code: platform.name,
        environment: '',
        status: platform.is_active ? 'Connected' : 'Inactive',
      }));
      this.platforms_loaded = true;
    },

    async fetch_platform_configurations(token) {
      const result = await fetch_platform_configurations_api(this.build_headers(token)).catch(() => null);
      if (!result?.ok) {
        this.platform_configurations = [];
        this.configurations_loaded = true;
        this.$notyf.error(get_api_error_message(result?.response_data, 'Unable to load platform configurations right now.'));
        return;
      }

      this.platform_configurations = result.data;
      this.configurations_loaded = true;
    },

    build_launch_test_rows() {
      this.launch_test_rows = build_launch_test_rows(this.displayed_configurations);
      this.launch_tests_loaded = true;
    },

    initialize_selected_configuration() {
      const already_selected = this.selected_configuration_id
        ? this.displayed_configurations.find((item) => item.id === this.selected_configuration_id)
        : null;
      if (already_selected) return this.set_selected_configuration(already_selected);

      const first_configuration = this.displayed_configurations[0] || null;
      if (first_configuration) this.set_selected_configuration(first_configuration);
    },

    set_active_tab(tab_name) {
      this.active_tab = tab_name;
    },

    set_selected_configuration(configuration) {
      this.selected_configuration_id = configuration.id;
      this.selected_configuration = configuration;
      this.selected_configuration_snapshot = { ...configuration };
      this.readiness_result = create_selected_readiness_result(configuration);
      this.clear_deployment_handover();
    },

    get_status_badge_class(status) {
      return get_status_badge_class(status);
    },

    format_status_label(value) {
      return format_status_label(value);
    },

    prepare_add_platform_modal() {
      this.platform_form = build_default_platform_form();
    },

    prepare_add_config_modal(configuration = null) {
      this.editing_configuration_id = configuration?.id || null;
      this.platform_config_step = 1;
      this.platform_config_form = build_default_platform_config_form(configuration);
    },

    go_to_platform_config_step(step_number) {
      if (step_number < 1 || step_number > 4) return;
      if (step_number > this.platform_config_step && !this.validate_platform_config_step(this.platform_config_step)) return;
      this.platform_config_step = step_number;
    },

    next_platform_config_step() {
      if (!this.validate_platform_config_step(this.platform_config_step)) return;
      if (this.platform_config_step < 4) this.platform_config_step += 1;
    },

    previous_platform_config_step() {
      if (this.platform_config_step > 1) this.platform_config_step -= 1;
    },

    normalize_platform_name(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    },

    handle_platform_config_platform_change() {
      const selected_platform = this.platform_options.find(
        (platform_option) => String(platform_option.id) === String(this.platform_config_form.platform),
      );
      if (!selected_platform) return;

      this.platform_config_form.platform_id = selected_platform.id;
      if (!this.platform_config_form.app_code) this.platform_config_form.app_code = selected_platform.label;

      const normalized_name = this.normalize_platform_name(selected_platform.label);
      this.platform_config_form.system = normalized_name;
      this.platform_config_form.signer_salt = normalized_name ? `${normalized_name}-aic-one` : '';
    },

    parse_routing_rules_text() {
      let parsed_value = null;

      try {
        parsed_value = JSON.parse(this.platform_config_form.routing_rules_text || '[]');
      } catch (error) {
        return null;
      }

      return Array.isArray(parsed_value) ? parsed_value : null;
    },

    notify_platform_config_validation(is_valid, message) {
      if (!is_valid) this.$notyf.info(message);
      return is_valid;
    },

    has_selected_platform_config_platform() {
      return Boolean(this.platform_config_form.platform_id || this.platform_config_form.platform);
    },

    validate_platform_config_step_one() {
      if (!this.notify_platform_config_validation(this.has_selected_platform_config_platform(), 'Please select a platform.')) return false;
      if (!this.notify_platform_config_validation(Boolean(this.platform_config_form.app_code.trim()), 'App Code is required.')) return false;
      return this.notify_platform_config_validation(Boolean(this.platform_config_form.environment), 'Please select an environment.');
    },

    validate_platform_config_step_two() {
      if (!this.notify_platform_config_validation(Boolean(this.platform_config_form.base_url.trim()), 'Base URL is required.')) return false;
      return this.notify_platform_config_validation(Boolean(this.platform_config_form.launch_url.trim()), 'Launch URL is required.');
    },

    validate_platform_config_step_three() {
      if (!this.notify_platform_config_validation(Boolean(this.platform_config_form.signer_salt.trim()), 'Signer Salt is required.')) return false;
      if (!this.notify_platform_config_validation(!Number.isNaN(Number(this.platform_config_form.max_allowed_skew)), 'Max Allowed Skew must be a valid number.')) return false;
      return this.notify_platform_config_validation(Boolean(this.platform_config_form.system.trim()), 'System Code is required.');
    },

    validate_platform_config_step_four() {
      return this.notify_platform_config_validation(Boolean(this.parse_routing_rules_text()), 'Routing Rules must be a valid JSON array.');
    },

    validate_platform_config_step(step_number) {
      switch (step_number) {
        case 1:
          return this.validate_platform_config_step_one();
        case 2:
          return this.validate_platform_config_step_two();
        case 3:
          return this.validate_platform_config_step_three();
        case 4:
          return this.validate_platform_config_step_four();
        default:
          return true;
      }
    },

    prepare_access_modal() {
      this.access_form = build_default_access_form(this.selected_configuration);
    },

    open_view_checklist_modal(configuration) {
      this.set_selected_configuration(configuration);
      this.deployment_checklist_modal_config = configuration;
      this.fetch_deployment_handover();
    },

    clear_deployment_handover() {
      this.deployment_handover = null;
      this.deployment_values_revealed = false;
      this.revealed_deployment_values = null;
    },

    build_selected_handover_payload() {
      return build_deployment_handover_payload(this.selected_config);
    },

    async submit_platform_form() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      try {
        const result = await submit_platform_form_api(
          this.build_headers(token),
          build_platform_form_payload(this.platform_form),
        );
        if (!result.ok) {
          this.$notyf.info(get_api_error_message(result.response_data, 'Create platform endpoint is not ready yet.'));
          return;
        }

        this.$notyf.success(result.response_data?.message || 'Platform added successfully.');
        await this.fetch_platforms(token);
        this.$refs.close_add_platform_modal_btn?.click();
      } catch (error) {
        this.$notyf.error('Unable to add platform right now.');
      }
    },

    validate_all_platform_config_steps() {
      return [1, 2, 3, 4].every((step_number) => this.validate_platform_config_step(step_number));
    },

    get_selected_platform_config_identity() {
      return {
        platform_id: this.platform_config_form.platform_id || this.platform_config_form.platform,
        app_code: this.platform_config_form.app_code,
      };
    },

    find_saved_platform_configuration(selected_identity) {
      return this.platform_configurations.find((configuration) => (
        String(configuration.platform_id || configuration.platform) === String(selected_identity.platform_id)
        && configuration.app_code === selected_identity.app_code
      )) || null;
    },

    async handle_platform_config_save_success(token, selected_identity, response_data) {
      this.$notyf.success(response_data?.message || 'Platform configuration saved.');
      await this.fetch_platform_configurations(token);
      this.build_launch_test_rows();

      const saved_configuration = this.find_saved_platform_configuration(selected_identity);
      if (saved_configuration) this.set_selected_configuration(saved_configuration);

      this.initialize_selected_configuration();
      this.prepare_add_config_modal();
      this.$refs.close_add_platform_config_modal_btn?.click();
    },

    async submit_platform_config_form() {
      const token = this.require_token_or_redirect();
      if (!token) return;
      if (!this.validate_all_platform_config_steps()) return;

      const routing_rules = this.parse_routing_rules_text();
      if (!routing_rules) {
        this.$notyf.info('Routing Rules must be a valid JSON array.');
        return;
      }
      const selected_identity = this.get_selected_platform_config_identity();

      try {
        const result = await submit_platform_config_form_api(
          this.build_headers(token),
          build_platform_config_payload(this.platform_config_form, routing_rules, this.editing_configuration_id),
        );
        if (!result.ok) {
          this.$notyf.info(get_api_error_message(result.response_data, 'Create platform config endpoint is not ready yet.'));
          return;
        }

        await this.handle_platform_config_save_success(token, selected_identity, result.response_data);
      } catch (error) {
        this.$notyf.error('Unable to save the platform configuration right now.');
      }
    },

    async submit_platform_access_form() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      try {
        const result = await submit_platform_access_form_api(
          this.build_headers(token),
          build_platform_access_payload(this.access_form),
        );
        if (!result.ok) {
          this.$notyf.info(get_api_error_message(result.response_data, 'Assign platform access endpoint is not ready yet.'));
          return;
        }

        this.$notyf.success(result.response_data?.message || 'Platform access assigned.');
        // await this.fetch_platform_access_assignments(token);
        this.$refs.close_assign_platform_access_modal_btn?.click();
      } catch (error) {
        this.$notyf.error('Unable to assign platform access right now.');
      }
    },

    get_readiness_target_configuration(configuration) {
      return configuration || this.selected_configuration;
    },

    is_valid_readiness_target(configuration) {
      return Boolean(configuration?.platform && configuration?.app_code);
    },

    handle_readiness_check_result(result, target_configuration) {
      if (!result.ok) {
        this.$notyf.error(get_api_error_message(result.response_data, 'Readiness check failed.'));
        this.readiness_result = create_failed_readiness_result(this.readiness_result);
        return;
      }

      const data = result.response_data?.data || {};
      this.readiness_result = create_completed_readiness_result(target_configuration, data);
      this.$notyf.success(result.response_data?.message || 'Readiness check completed.');
    },

    async run_readiness_check(configuration = null) {
      const token = this.require_token_or_redirect();
      if (!token) return;

      const target_configuration = this.get_readiness_target_configuration(configuration);
      if (!this.is_valid_readiness_target(target_configuration)) {
        this.$notyf.info('Select a configuration before running a readiness check.');
        return;
      }

      this.set_selected_configuration(target_configuration);
      this.readiness_loading = true;

      try {
        const result = await run_readiness_check_api(this.build_headers(token), {
          platform: target_configuration.platform,
          app_code: target_configuration.app_code,
        });
        this.handle_readiness_check_result(result, target_configuration);
      } catch (error) {
        this.$notyf.error('Unable to run the readiness check right now.');
      } finally {
        this.readiness_loading = false;
      }
    },

    async test_launch(configuration) {
      const token = this.require_token_or_redirect();
      if (!token) return;

      this.launch_loading_id = configuration.id;

      try {
        const result = await test_launch_api(this.build_headers(token), {
          platform: configuration.platform,
          app_code: configuration.app_code,
        });
        if (!result.ok) {
          this.$notyf.error(get_api_error_message(result.response_data, 'Launch test failed.'));
          return;
        }

        if (!result.response_data?.data?.redirect_url) {
          this.$notyf.error('Launch response did not include a redirect URL.');
          return;
        }

        const safe_redirect_url = get_safe_redirect_url(result.response_data.data.redirect_url);
        if (!safe_redirect_url) {
          this.$notyf.error('Launch response included an invalid redirect URL.');
          return;
        }

        window.location.assign(safe_redirect_url);
      } catch (error) {
        this.$notyf.error('Unable to launch the configured platform right now.');
      } finally {
        this.launch_loading_id = null;
      }
    },

    get_display_secret(secret_value = {}) {
      return get_display_secret(secret_value);
    },

    get_copy_secret(secret_value = {}) {
      return get_copy_secret(secret_value);
    },

    copy_to_clipboard(value, success_message) {
      return navigator.clipboard.writeText(value)
        .then(() => {
          this.$notyf.success(success_message);
        })
        .catch(() => {
          this.$notyf.error('Copy failed. Please try again.');
        });
    },

    async copy_deployment_value(entry) {
      const copy_value = this.get_copy_secret(entry.value);
      await this.copy_to_clipboard(copy_value, `${entry.label} copied.`);

      if (!entry.value.one_time_value) {
        this.$notyf.info('A masked deployment value was copied because no one-time secret value was returned.');
      }
    },

    async copy_deployment_checklist(configuration = null) {
      const target_configuration = configuration || this.selected_configuration;
      if (!target_configuration) {
        this.$notyf.info('Select a configuration before copying env values.');
        return;
      }

      if (this.deployment_handover && target_configuration.id === this.selected_configuration?.id) {
        await this.copy_deployment_env_values();
        return;
      }

      const entries = [
        `Platform: ${target_configuration.platform_name}`,
        `Environment: ${target_configuration.environment}`,
        `App Code: ${target_configuration.app_code}`,
        ...this.checklist_entries.map((entry) => `${entry.label}=${this.get_copy_secret(entry.value)}`),
      ];

      await this.copy_to_clipboard(entries.join('\n'), 'Env values copied.');

      const has_unmasked_secret = this.checklist_entries.some((entry) => entry.value.one_time_value);
      if (!has_unmasked_secret) {
        this.$notyf.info('Masked values were copied because no one-time secret payload was returned by the API.');
      }
    },

    async copy_deployment_env_values() {
      if (!this.deployment_handover) {
        this.$notyf.info('View the DevOps handover before copying env values.');
        return;
      }

      const entries = [
        `Platform: ${this.handover_platform_name}`,
        `Environment: ${this.handover_environment}`,
        `App Code: ${this.handover_app_code}`,
        ...this.displayed_deployment_env_values.map((entry) => `${entry.key} = ${entry.value}`),
      ];

      await this.copy_to_clipboard(entries.join('\n'), 'Env values copied.');
    },

    async fetch_deployment_handover() {
      if (!this.selected_config) {
        await Swal.fire({
          icon: 'warning',
          title: 'No configuration selected',
          text: 'Please select a platform configuration first.',
          confirmButtonText: 'OK',
        });
        return;
      }

      const token = this.require_token_or_redirect();
      if (!token) return;

      this.deployment_handover_loading = true;
      this.deployment_values_revealed = false;
      this.revealed_deployment_values = null;

      try {
        const result = await fetch_platform_config_deployment_handover_api(
          this.build_headers(token),
          this.build_selected_handover_payload(),
        );

        if (!result.ok) {
          await Swal.fire({
            icon: 'error',
            title: 'Handover unavailable',
            text: get_api_error_message(
              result.response_data,
              'Unable to load the DevOps handover for this configuration.',
            ),
          });
          return;
        }

        this.deployment_handover = result.response_data?.data || {};
        this.$notyf.success(result.response_data?.message || 'DevOps handover ready.');
      } catch (error) {
        await Swal.fire({
          icon: 'error',
          title: 'Handover unavailable',
          text: 'Unable to load the DevOps handover right now.',
        });
      } finally {
        this.deployment_handover_loading = false;
      }
    },

    has_selected_deployment_config() {
      return Boolean(this.selected_config);
    },

    notify_missing_deployment_config() {
      this.$notyf.info('Select a configuration before revealing deployment values.');
    },

    async confirm_reveal_deployment_values() {
      const confirmation = await Swal.fire({
        icon: 'warning',
        title: 'Reveal deployment values?',
        text: 'This will show sensitive deployment values. Only reveal them when handing over to DevOps. This action may be audited.',
        showCancelButton: true,
        confirmButtonText: 'Reveal values',
        cancelButtonText: 'Cancel',
      });

      return confirmation.isConfirmed;
    },

    handle_reveal_deployment_values_result(result) {
      if (!result.ok) {
        this.$notyf.error(get_api_error_message(result.response_data, 'Unable to reveal deployment values.'));
        return;
      }

      const data = result.response_data?.data || {};
      this.revealed_deployment_values = data.env_values || data.deployment_values || data.values || data;
      this.deployment_values_revealed = true;
      this.$notyf.success(result.response_data?.message || 'Deployment values revealed for this session.');
    },

    async reveal_deployment_values() {
      if (!this.has_selected_deployment_config()) {
        this.notify_missing_deployment_config();
        return;
      }

      const confirmed = await this.confirm_reveal_deployment_values();
      if (!confirmed) return;

      const token = this.require_token_or_redirect();
      if (!token) return;

      try {
        const result = await reveal_platform_config_deployment_values_api(
          this.build_headers(token),
          this.build_selected_handover_payload(),
        );

        this.handle_reveal_deployment_values_result(result);
      } catch {
        this.$notyf.error('Unable to reveal deployment values right now.');
      }
    },

    view_configuration(configuration) {
      this.set_selected_configuration(configuration);
      this.active_tab = 'Configurations';
    },

    open_edit_configuration(configuration) {
      this.set_selected_configuration(configuration);
      this.prepare_add_config_modal(configuration);
    },
  },
};
